import { LOCAL_STORAGE_KEYS } from '../../consts/localStorage';
import { QUERY_PARAMS, QUERY_PARAM_VALUES, ROUTES } from '../../routes/consts';
import {
  GITHUB_MT_ERROR_CODES,
  MARKETPLACE_ERROR_CODES,
  MARKETPLACE_PHASES,
  MARKETPLACE_STATE_TTL_MS,
  MarketplaceErrorCode,
  MarketplacePhase,
} from './consts';
import {
  GithubMtErrorCode,
  MarketplaceInstallationStateEntry,
  MarketplaceUrlPayload,
} from './types';

const KNOWN_MARKETPLACE_ERROR_CODES = new Set<string>(
  Object.values(MARKETPLACE_ERROR_CODES),
);

const KNOWN_GITHUB_MT_ERROR_CODES = new Set<string>(
  Object.values(GITHUB_MT_ERROR_CODES),
);

/**
 * Type guard for the GitHub MT initial-install error code. Narrows a raw
 * string to `GithubMtErrorCode` when present in `GITHUB_MT_ERROR_CODES`.
 */
export const isGithubMtErrorCode = (
  value: string | null | undefined,
): value is GithubMtErrorCode =>
  typeof value === 'string' && KNOWN_GITHUB_MT_ERROR_CODES.has(value);

/**
 * Read the GitHub MT initial-install error code from the URL and validate it
 * against `GITHUB_MT_ERROR_CODES`. Returns the typed code or `undefined` when
 * missing or unrecognized — callers must treat unknown values as "no error"
 * so the wizard does not show a misleading error UI for arbitrary query
 * strings. Reads `window.location.search` directly per the project's
 * URL-search-param convention.
 */
export const getValidatedInstallationErrorCode = ():
  | GithubMtErrorCode
  | undefined => {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(QUERY_PARAMS.ERROR_CODE);

  return isGithubMtErrorCode(raw) ? raw : undefined;
};

/**
 * Parse the GitHub Marketplace landing query params from a search string.
 * Returns a marketplace landing payload only when `source=marketplace` AND
 * both `installation_id` and `state` are present and well-formed.
 */
export const parseMarketplaceParamsFromSearch = (
  search: string,
): {
  isMarketplaceLanding: boolean;
  installationId: number | undefined;
  state: string | undefined;
  hostingType: string | undefined;
} => {
  const params = new URLSearchParams(search);
  const source = params.get(QUERY_PARAMS.SOURCE);
  const installationIdRaw = params.get(QUERY_PARAMS.INSTALLATION_ID);
  const state = params.get(QUERY_PARAMS.STATE) || undefined;
  const hostingType = params.get(QUERY_PARAMS.HOSTING_TYPE) || undefined;
  const installationId = Number(installationIdRaw);
  const hasValidInstallationId =
    !!installationIdRaw && Number.isFinite(installationId);

  const isMarketplaceLanding =
    source === QUERY_PARAM_VALUES.SOURCE.MARKETPLACE &&
    hasValidInstallationId &&
    !!state;

  return {
    isMarketplaceLanding,
    installationId: hasValidInstallationId ? installationId : undefined,
    state,
    hostingType,
  };
};

/**
 * Read the persisted marketplace installation-state entry from localStorage.
 * Returns `undefined` if missing, malformed, or older than {@link MARKETPLACE_STATE_TTL_MS}
 * (the BE TTL is 1h; we use a 50min ceiling for safety). Expired entries are
 * proactively cleared so a stale payload never blocks future flows.
 */
export const readMarketplaceInstallationStateFromLocalStorage = ():
  | MarketplaceInstallationStateEntry
  | undefined => {
  const raw = localStorage.getItem(
    LOCAL_STORAGE_KEYS.MT_MARKETPLACE_INSTALLATION_STATE,
  );

  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as MarketplaceInstallationStateEntry;
    const isShapeValid =
      typeof parsed?.installationId === 'number' &&
      typeof parsed?.state === 'string' &&
      typeof parsed?.createdAt === 'string';

    if (!isShapeValid) {
      clearMarketplaceInstallationState();

      return undefined;
    }

    const createdAtMs = new Date(parsed.createdAt).getTime();
    const isExpired =
      !Number.isFinite(createdAtMs) ||
      Date.now() - createdAtMs > MARKETPLACE_STATE_TTL_MS;

    if (isExpired) {
      clearMarketplaceInstallationState();

      return undefined;
    }

    return parsed;
  } catch {
    clearMarketplaceInstallationState();

    return undefined;
  }
};

/**
 * Persist the BE-generated marketplace state token alongside the
 * GitHub installation ID. Stamps `createdAt` so the entry can be expired.
 */
export const writeMarketplaceInstallationState = (
  payload: MarketplaceUrlPayload,
): void => {
  const stateToWrite: MarketplaceInstallationStateEntry = {
    installationId: payload.installationId,
    state: payload.state,
    createdAt: new Date().toISOString(),
  };

  localStorage.setItem(
    LOCAL_STORAGE_KEYS.MT_MARKETPLACE_INSTALLATION_STATE,
    JSON.stringify(stateToWrite),
  );
};

/**
 * Remove the persisted marketplace installation-state entry. Safe to call
 * when no entry exists.
 */
export const clearMarketplaceInstallationState = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.MT_MARKETPLACE_INSTALLATION_STATE);
};

/**
 * Read the error code returned by `POST /mt/installation/create`. The endpoint
 * uses `{ detail: '<CODE>' }` (FastAPI-style), distinct from the legacy
 * `{ error: { message } }` shape used elsewhere in the app.
 */
export const getMarketplaceErrorCode = (
  err: unknown,
): { code: MarketplaceErrorCode; statusCode: number | undefined } => {
  const response = (
    err as { response?: { status?: number; data?: { detail?: string } } }
  )?.response;
  const statusCode = response?.status;
  const detail = response?.data?.detail;

  if (statusCode === 503) {
    return {
      code: MARKETPLACE_ERROR_CODES.SERVICE_UNAVAILABLE,
      statusCode,
    };
  }

  if (statusCode === 409) {
    if (
      detail ===
      MARKETPLACE_ERROR_CODES.INSTALLATION_ALREADY_EXISTS_IN_WORKSPACE
    ) {
      return {
        code: MARKETPLACE_ERROR_CODES.INSTALLATION_ALREADY_EXISTS_IN_WORKSPACE,
        statusCode,
      };
    }

    return {
      code: MARKETPLACE_ERROR_CODES.INSTALLATION_OWNED_BY_OTHER_WORKSPACE,
      statusCode,
    };
  }

  if (detail && KNOWN_MARKETPLACE_ERROR_CODES.has(detail)) {
    return {
      code: detail as MarketplaceErrorCode,
      statusCode,
    };
  }

  return {
    code: MARKETPLACE_ERROR_CODES.GENERIC,
    statusCode,
  };
};

/**
 * Map a React Query mutation snapshot to the wizard's marketplace phase.
 * Pure: keeps the component free of branching logic and easy to reason about.
 */
export const deriveMarketplacePhaseFromMutation = (params: {
  isMarketplaceFlow: boolean;
  status: 'idle' | 'pending' | 'success' | 'error';
  error: unknown;
}): {
  phase: MarketplacePhase;
  errorCode: MarketplaceErrorCode | undefined;
} => {
  if (!params.isMarketplaceFlow) {
    return { phase: MARKETPLACE_PHASES.IDLE, errorCode: undefined };
  }

  if (params.status === 'pending' || params.status === 'idle') {
    return { phase: MARKETPLACE_PHASES.LOADING, errorCode: undefined };
  }

  if (params.status === 'success') {
    return { phase: MARKETPLACE_PHASES.SUCCESS, errorCode: undefined };
  }

  const { code } = getMarketplaceErrorCode(params.error);

  if (
    code === MARKETPLACE_ERROR_CODES.INSTALLATION_ALREADY_EXISTS_IN_WORKSPACE
  ) {
    return {
      phase: MARKETPLACE_PHASES.ALREADY_INSTALLED,
      errorCode: undefined,
    };
  }

  return { phase: MARKETPLACE_PHASES.ERROR, errorCode: code };
};

/**
 * Build the marketplace resume URL (path + query) from a stored entry,
 * preserving the canonical query-param shape the wizard listens for.
 *
 * Lands directly on step 2 (Installation Complete), which is where the
 * marketplace flow renders its loading / success / error UI.
 */
export const buildMarketplaceResumeUrl = (
  entry: MarketplaceInstallationStateEntry,
): string => {
  const params = new URLSearchParams({
    [QUERY_PARAMS.HOSTING_TYPE]: 'saas',
    [QUERY_PARAMS.INSTALLATION_ID]: String(entry.installationId),
    [QUERY_PARAMS.SOURCE]: QUERY_PARAM_VALUES.SOURCE.MARKETPLACE,
    [QUERY_PARAMS.STATE]: entry.state,
  });

  return `${ROUTES.GITHUB_MT_INSTALLATION_STEP_2}?${params.toString()}`;
};

/**
 * Build a URL identical to the current location but with the marketplace
 * landing query params (`source`, `installation_id`, `state`) removed.
 * Used after the wizard reaches a terminal phase to keep the URL clean
 * without losing any unrelated query state. Pure — caller is responsible
 * for navigation.
 */
export const buildUrlWithoutMarketplaceParams = (
  pathname: string,
  search: string,
): string => {
  const cleaned = new URLSearchParams(search);

  cleaned.delete(QUERY_PARAMS.SOURCE);
  cleaned.delete(QUERY_PARAMS.INSTALLATION_ID);
  cleaned.delete(QUERY_PARAMS.STATE);

  const cleanedSearch = cleaned.toString();

  return cleanedSearch ? `${pathname}?${cleanedSearch}` : pathname;
};

/**
 * After onboarding completes, prefer resuming the in-progress marketplace
 * flow (if any) over the default home redirect. Returns the destination path.
 */
export const getPostOnboardingDestination = (): string => {
  const state = readMarketplaceInstallationStateFromLocalStorage();

  if (state) {
    return buildMarketplaceResumeUrl(state);
  }

  return ROUTES.ROOT;
};
